import { EPaletteModes, IModeImages } from '@/types/theme-packs.types';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import UploadLarge from '@ui/uploads/upload-large';
import handleImageUploadErrors from '@ui/image-upload/handle-image-upload-errors';
import {
  GetUploadEndpoint,
  getUploadFilename,
  UploadFileToProvider,
  SetItemContentType,
} from '@ui/uploads/validations';
import { isObject } from 'underscore';
import { useState } from 'react';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';

type ThemePackImageUploadProps = {
  mode: EPaletteModes;
  onChange: (mode: EPaletteModes, modeImages: IModeImages) => void;
  modeImages: IModeImages | undefined;
};

const uploadDisplayProps = {
  [EPaletteModes.Light]: {
    icon: ICONS.LIGHT_MODE,
    name: 'Light theme',
  },
  [EPaletteModes.Dark]: {
    icon: ICONS.DARK_MODE,
    name: 'Dark theme',
  },
};

const ThemePackImageUpload = ({ mode, onChange, modeImages }: ThemePackImageUploadProps) => {
  const [uploadingHorizontal, setUpLoadingHorizontal] = useState(false);
  const [uploadingWordmark, setUpLoadingWordmark] = useState(false);

  const handleFile = async (file: File | FileList, type: string) => {
    try {
      if (file instanceof File) {
        if (type === 'horizontal') {
          setUpLoadingHorizontal(true);
        } else if (type === 'wordmark') {
          setUpLoadingWordmark(true);
        }

        const endpoint = await GetUploadEndpoint({
          filename: getUploadFilename(file.name),
        });

        if (isObject(endpoint) && endpoint.url) {
          const finalUrl = await UploadFileToProvider(
            {
              url: endpoint.url,
              fields: endpoint.fields,
              cdnHost: endpoint?.cdnHost,
            },
            file
          );

          SetItemContentType({ filename: finalUrl, contentType: file.type }).catch(console.error);

          if (finalUrl) {
            const updatedImages = {
              ...(modeImages || {}),
              [type]: finalUrl,
            };
            onChange(mode, updatedImages);
            return Promise.resolve();
          }
        }
      }
    } catch (error) {
      console.error(error);
      handleImageUploadErrors(error);
    } finally {
      if (type === 'horizontal') {
        setUpLoadingHorizontal(false);
      } else if (type === 'wordmark') {
        setUpLoadingWordmark(false);
      }
    }
  };

  return (
    <Container
      className="radius-12 gap-8 jc-space-between padding-l-12 padding-v-8 padding-r-8 flex fa-center"
      gap="8px"
      // width="299px"
      width="fit-content"
      height="80px"
      background="var(--gray-900)"
    >
      <Container className="flex fd-row fa-center ai-center jc-space-between" gap="8px">
        <Container className="flex fd-column ai-center padding-h-4 padding-r-8" gap="5px">
          <Icon size={16} name={uploadDisplayProps[mode].icon} />
          <Text className="C2">{uploadDisplayProps[mode].name}</Text>
        </Container>
        <Container
          background="transparent"
          borderStyle={modeImages?.wordmark?.length ? 'solid' : 'dashed'}
          height="64px"
          width="64px"
          borderWidth="1px"
          borderRadius="8px"
          borderColor="var(--global-cont-4)"
        >
          <UploadLarge
            key={modeImages?.wordmark}
            wrapperClassNames="square-40 radius-100"
            displayImageUrl={modeImages?.wordmark}
            style={{ borderRadius: '8px', backgroundSize: 'cover' }}
            hideIconIfImage
            uploading={uploadingWordmark}
            maxSizeInBytes={5 * 1024 * 1024} //5 MB
            CustomTitle={
              <Container className="flex fd-column jc-center ai-center" gap="4px">
                <Icon name={ICONS.IMAGE_UPLOAD} />
                <Container className="flex ai-center" gap="2px">
                  <Text className="C1" color="var(--global-ico-text-3)">
                    Icon
                  </Text>
                  <TooltipPortal
                    tooltipContent={
                      <Text className="C1" color="var(--gray-true-dark-100)">
                        Displayed in the navigation bar.
                      </Text>
                    }
                    animationType="slide-up"
                    styles={{ padding: '0 4px' }}
                    tailSize={0}
                    verticalOffset={-6}
                  >
                    <Container className="margin-t--2">
                      <Icon color={'var(--global-ico-text-3)'} name={ICONS.INFO_CIRCLE} size={10} />
                    </Container>
                  </TooltipPortal>
                </Container>
              </Container>
            }
            hidePlaceholderImage
            allowedFileTypes={['image/jpg', 'image/jpeg', 'image/png', 'image/gif']}
            onFile={(file) => handleFile(file, 'wordmark')}
            deletable
            actionButtonsType="cover"
            onDelete={() => {
              const updatedImages = {
                ...modeImages,
                wordmark: undefined,
              };
              onChange(mode, updatedImages);
              return Promise.resolve();
            }}
          />
        </Container>

        <Container
          background="transparent"
          borderStyle={modeImages?.horizontal?.length ? 'solid' : 'dashed'}
          height="64px"
          width="112px"
          borderWidth="1px"
          borderRadius="8px"
          borderColor="var(--global-cont-4)"
        >
          <UploadLarge
            key={modeImages?.horizontal}
            wrapperClassNames="square-40 radius-100"
            displayImageUrl={modeImages?.horizontal}
            style={{ borderRadius: '8px', backgroundSize: 'cover' }}
            hideIconIfImage
            uploading={uploadingHorizontal}
            maxSizeInBytes={5 * 1024 * 1024} //5 MB
            CustomTitle={
              <Container className="flex fd-column jc-center ai-center" gap="4px">
                <Icon name={ICONS.IMAGE_UPLOAD} />
                <Container className="flex ai-center" gap="2px">
                  <Text className="C1" color="var(--global-ico-text-3)">
                    Logo
                  </Text>
                  <TooltipPortal
                    tooltipContent={
                      <Text className="C1" color="var(--gray-true-dark-100)">
                        Displayed on registration.
                      </Text>
                    }
                    animationType="slide-up"
                    styles={{ padding: '0 4px' }}
                    tailSize={0}
                    verticalOffset={-6}
                  >
                    <Container className="margin-t--2">
                      <Icon color={'var(--global-ico-text-3)'} name={ICONS.INFO_CIRCLE} size={10} />
                    </Container>
                  </TooltipPortal>
                </Container>
              </Container>
            }
            hidePlaceholderImage
            allowedFileTypes={['image/jpg', 'image/jpeg', 'image/png', 'image/gif']}
            onFile={(file) => handleFile(file, 'horizontal')}
            deletable
            actionButtonsType="cover"
            onDelete={() => {
              const updatedImages = {
                ...modeImages,
                horizontal: undefined,
              };
              onChange(mode, updatedImages);
              return Promise.resolve();
            }}
          />
        </Container>
      </Container>
    </Container>
  );
};

export default ThemePackImageUpload;
